/**
 * Khối "Nhật ký sự kiện" trên chi tiết phiếu (chuẩn Feedback EMS v2 §13) — chỉ quản trị/dev.
 * Một dòng thời gian duy nhất: sự kiện trình duyệt + request máy chủ + nhật ký hoạt động dữ liệu
 * của người gửi trong 15 phút trước → 2 phút sau lúc tạo phiếu; lỗi tô đỏ; nhóm theo mã request.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";

export interface TimelineItem {
  id: string;
  at: string;
  origin: "client" | "server" | "activity" | "snapshot" | "feedback";
  kind: string;
  name: string;
  level: "info" | "warn" | "error";
  status?: number | null;
  durationMs?: number | null;
  requestId?: string | null;
  sessionId?: string | null;
  detail?: string | null;
}

export const TIMELINE_KIND_LABELS: Record<string, string> = {
  route: "Chuyển trang",
  click: "Bấm",
  submit: "Gửi biểu mẫu",
  api: "Gọi API",
  console: "Console",
  error: "Lỗi",
  server_request: "Máy chủ",
  auth: "Đăng nhập/xuất",
  activity: "Dữ liệu",
  feedback: "Phiếu",
};

const KIND_STYLES: Record<string, string> = {
  route: "bg-sky-50 text-sky-700 border-sky-200",
  click: "bg-slate-100 text-slate-700 border-slate-200",
  submit: "bg-indigo-50 text-indigo-700 border-indigo-200",
  api: "bg-violet-50 text-violet-700 border-violet-200",
  console: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-rose-100 text-rose-800 border-rose-300",
  server_request: "bg-emerald-50 text-emerald-700 border-emerald-200",
  auth: "bg-teal-50 text-teal-700 border-teal-200",
  activity: "bg-orange-50 text-orange-700 border-orange-200",
  feedback: "bg-blue-600 text-white border-blue-600",
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "--:--:--";
  const pad = (n: number, l = 2) => String(n).padStart(l, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function shortId(id?: string | null): string {
  return id ? id.slice(0, 8) : "";
}

interface TimelineGroup {
  key: string;
  requestId: string | null;
  items: TimelineItem[];
}

/** Nhóm theo mã request: các sự kiện cùng request (gọi API ở trình duyệt + xử lý ở máy chủ) gom một khối */
export function groupTimelineByRequest(items: TimelineItem[]): TimelineGroup[] {
  const groups: TimelineGroup[] = [];
  const byRequest = new Map<string, TimelineGroup>();
  for (const it of items) {
    if (it.requestId && it.origin !== "feedback") {
      const existing = byRequest.get(it.requestId);
      if (existing) {
        existing.items.push(it);
        continue;
      }
      const g: TimelineGroup = { key: `req-${it.requestId}`, requestId: it.requestId, items: [it] };
      byRequest.set(it.requestId, g);
      groups.push(g);
    } else {
      groups.push({ key: `one-${it.id}`, requestId: null, items: [it] });
    }
  }
  return groups;
}

export function timelineToDevText(data: {
  code?: string | null;
  from: string;
  to: string;
  sessionId?: string | null;
  requestId?: string | null;
  items: TimelineItem[];
}): string {
  const lines = [
    `Phiếu #${data.code || ""} — Nhật ký sự kiện ${data.from} → ${data.to}`,
    `session=${data.sessionId || "-"} request=${data.requestId || "-"}`,
    "",
  ];
  for (const it of data.items) {
    const parts = [
      it.at,
      it.level.toUpperCase().padEnd(5),
      `[${it.origin}/${it.kind}]`,
      it.name,
      it.status != null ? `status=${it.status}` : "",
      it.durationMs != null ? `${it.durationMs}ms` : "",
      it.requestId ? `req=${it.requestId}` : "",
    ].filter(Boolean);
    lines.push(parts.join(" "));
    if (it.detail) lines.push(`    ${it.detail.replace(/\n/g, "\n    ")}`);
  }
  return lines.join("\n");
}

function TimelineRow({ item }: { item: TimelineItem }) {
  const isError = item.level === "error";
  const isWarn = item.level === "warn";
  return (
    <li
      data-testid="fb-timeline-item"
      data-kind={item.kind}
      data-level={item.level}
      className={cn(
        "flex items-start gap-2 px-2.5 py-1.5 rounded-lg text-xs min-w-0",
        isError ? "bg-rose-50 text-rose-900" : isWarn ? "bg-amber-50/70 text-amber-900" : "text-slate-700",
        item.origin === "feedback" && "bg-blue-50 text-blue-900 font-semibold",
      )}
    >
      <span className="font-mono tabular-nums text-[11px] text-slate-500 shrink-0 pt-0.5">{formatTime(item.at)}</span>
      <span
        className={cn(
          "shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide",
          isError && item.kind !== "feedback" ? "bg-rose-100 text-rose-800 border-rose-300" : KIND_STYLES[item.kind] || KIND_STYLES.click,
        )}
      >
        {TIMELINE_KIND_LABELS[item.kind] || item.kind}
      </span>
      <div className="min-w-0 flex-1">
        <div className="break-all">
          <span>{item.name}</span>
          {item.status != null && (
            <span
              className={cn(
                "ml-1.5 px-1 rounded font-mono text-[11px]",
                item.status >= 500 ? "bg-rose-200 text-rose-900" : item.status >= 400 ? "bg-amber-200 text-amber-900" : "bg-slate-100 text-slate-600",
              )}
            >
              {item.status}
            </span>
          )}
          {item.durationMs != null && <span className="ml-1.5 text-slate-400 font-mono text-[11px]">{item.durationMs}ms</span>}
        </div>
        {item.detail && <div className="mt-0.5 whitespace-pre-wrap break-all text-[11px] opacity-80 line-clamp-4">{item.detail}</div>}
      </div>
    </li>
  );
}

export function FeedbackEventTimeline({ feedbackId, code }: { feedbackId: string; code?: string | null }) {
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["feedback-timeline", feedbackId],
    queryFn: () => trpcClient.feedback.timeline.query({ id: feedbackId }),
    staleTime: 30_000,
  });

  const items: TimelineItem[] = useMemo(() => ((data as any)?.items || []) as TimelineItem[], [data]);
  const visible = useMemo(
    () => (onlyErrors ? items.filter((i) => i.level !== "info" || i.origin === "feedback") : items),
    [items, onlyErrors],
  );
  const groups = useMemo(() => groupTimelineByRequest(visible), [visible]);

  const handleCopy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(timelineToDevText({ ...(data as any), code, items }));
      setCopied(true);
      toast.success("Đã sao chép nhật ký sự kiện cho dev");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Không thể sao chép vào clipboard");
    }
  };

  const counts = (data as any)?.counts;

  return (
    <section
      data-testid="fb-event-timeline"
      className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-w-0"
    >
      <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/60">
        <History className="h-4 w-4 text-blue-600 shrink-0" />
        <h3 className="font-bold text-slate-800 text-sm sm:text-base">Nhật ký sự kiện</h3>
        {counts && (
          <span data-testid="fb-timeline-counts" className="text-xs text-slate-500">
            {counts.total} sự kiện · <span className={cn(counts.errors > 0 && "text-rose-600 font-semibold")}>{counts.errors} lỗi</span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            data-testid="fb-timeline-only-errors"
            aria-pressed={onlyErrors}
            onClick={() => setOnlyErrors((v) => !v)}
            className={cn(
              "min-h-9 px-2.5 rounded-lg border text-xs font-semibold cursor-pointer",
              onlyErrors ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
            )}
          >
            Chỉ lỗi
          </button>
          <button
            type="button"
            data-testid="fb-timeline-copy"
            onClick={handleCopy}
            disabled={!data}
            className="min-h-9 inline-flex items-center gap-1.5 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 cursor-pointer disabled:opacity-50"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            <span>Sao chép cho dev</span>
          </button>
        </div>
      </div>
      <div className="p-3 sm:p-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Đang tải nhật ký...
          </div>
        ) : error ? (
          <p className="text-sm text-rose-600">Không tải được nhật ký sự kiện.</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-slate-500">Không có sự kiện nào trong khoảng thời gian gửi phiếu.</p>
        ) : (
          <>
            <p className="text-[11px] text-slate-500 mb-2">
              Từ {formatTime((data as any).from)} đến {formatTime((data as any).to)}
              {(data as any).sessionId ? ` · phiên ${shortId((data as any).sessionId)}` : ""}
            </p>
            <ol data-testid="fb-timeline-list" className="space-y-1 max-h-[28rem] overflow-y-auto pr-1">
              {groups.map((g) =>
                g.requestId && g.items.length > 1 ? (
                  <li
                    key={g.key}
                    data-testid="fb-timeline-group"
                    data-request-id={g.requestId}
                    className={cn(
                      "rounded-lg border p-1",
                      g.items.some((i) => i.level === "error") ? "border-rose-300" : "border-slate-200",
                    )}
                  >
                    <div className="px-2 pt-0.5 text-[10px] font-mono text-slate-400">Request {shortId(g.requestId)}</div>
                    <ol className="space-y-0.5">
                      {g.items.map((it) => (
                        <TimelineRow key={it.id} item={it} />
                      ))}
                    </ol>
                  </li>
                ) : (
                  <TimelineRow key={g.key} item={g.items[0]} />
                ),
              )}
            </ol>
          </>
        )}
      </div>
    </section>
  );
}
